import {
	Amazon_Vendor,
	Required_Purposes,
} from "@/config/reach/common/constants/consent";
import { gptStore } from "@/store/gptStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import {
	isAdSizesAmazonValid,
	isDisplaySlotConfig,
	isVideoSlotConfig,
} from "@/ts/guards";
import type { AmazonModuleConfig, AuctionModule } from "@/ts/module";
import type { VendorName } from "@/ts/storeTypes/headerBiddingTypes";
import type {
	AdSizesAmazonConfig,
	AmazonBid,
	AmazonSlotMapEntry,
	ApsTagConfig,
	DisplaySlotConfig,
	VideoSlotConfig,
} from "@/ts/types";
import { createDependencyLoader } from "@/utils/dependencyLoader";
import { appendHtmlToHead } from "@/utils/dom";
import { getModules } from "@/utils/modules";
import {
	filterSlotBySize,
	getSizeForSlot,
	removeSizeMapping,
	removeUnneededSizes,
} from "@/utils/slots";
import { apsTag } from "@/vendors/apsTag";

export class AmazonTAM implements AuctionModule {
	private config: AmazonModuleConfig;
	name = "AmazonTAM" as const;

	constructor(config: AmazonModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		const { actions } = headerBiddingStore.getState();
		actions.addVendor(this.name);
		const [consentModule] = await getModules(["Consent"]);
		const consented = await consentModule.run({
			vendorId: Amazon_Vendor,
			purposes: Required_Purposes,
		});
		if (!consented) {
			return;
		}
		await amazonLoader.ensure(this.config.apstagConfig);
	}

	async run(
		auctionId: number,
		cmdSlots: (DisplaySlotConfig | VideoSlotConfig)[],
		timeout: number,
	) {
		// sync consent check in case of race-condition with async check in init()
		const [consentModule] = await getModules(["Consent"]);
		const consented = consentModule.run({
			vendorId: Amazon_Vendor,
			purposes: Required_Purposes,
			sync: true,
		});
		if (!consented) {
			return;
		}
		const { adSizesAmazon, maxSlotsPerAuction } = this.config;
		await amazonLoader.ensure(this.config.apstagConfig);
		const { pageAdUnitPath } = gptStore.getState();
		const { actions } = headerBiddingStore.getState();

		if (!isAdSizesAmazonValid(adSizesAmazon)) {
			actions.updateVendorAuctionStatus(auctionId, this.name, "failed");
			return;
		}

		actions.updateVendorAuctionStatus(auctionId, this.name, "running");

		const displaySlotSizeMatchesConfig = filterSlotBySize(adSizesAmazon);

		const displaySlots = cmdSlots
			.filter(isDisplaySlotConfig)
			.filter(displaySlotSizeMatchesConfig);
		const videoSlots = cmdSlots.filter(isVideoSlotConfig);

		const displaySlotsAmazon = prepareDisplaySlots(
			displaySlots,
			adSizesAmazon,
			pageAdUnitPath,
			timeout,
			maxSlotsPerAuction,
		);
		const videoSlotsAmazon = prepareVideoSlots(
			videoSlots,
			timeout,
			maxSlotsPerAuction,
		);

		const fetchBidsPromises: Promise<AmazonBid[]>[] = [];

		if (displaySlotsAmazon.slots.length > 0) {
			fetchBidsPromises.push(
				new Promise((resolve) => {
					window.apstag.fetchBids(displaySlotsAmazon, resolve);
				}),
			);
		}

		if (videoSlotsAmazon.slots.length > 0) {
			fetchBidsPromises.push(
				new Promise((resolve) => {
					window.apstag.fetchBids(videoSlotsAmazon, resolve);
				}),
			);
		}

		const allBids = await Promise.all(fetchBidsPromises);
		auctionCompletionCallback(auctionId, this.name)(allBids.flat());
	}
}

const auctionCompletionCallback =
	(auctionId: number, vendorName: VendorName) => (bids: AmazonBid[]) => {
		const { auctions, actions } = headerBiddingStore.getState();
		const isAuctionComplete = auctions[auctionId].done;

		if (!isAuctionComplete) {
			for (const bid of bids) {
				const { slotID, ...amazonBid } = bid;
				actions.updateSlotTargeting(auctionId, slotID, { ...amazonBid });
			}
		}

		actions.updateVendorAuctionStatus(auctionId, vendorName, "done");
	};

const prepareDisplaySlots = (
	displaySlots: DisplaySlotConfig[],
	adSizesAmazon: AdSizesAmazonConfig,
	pageAdUnitPath: string,
	timeout: number,
	maxSlotsPerAuction: number,
): {
	slots: AmazonSlotMapEntry[];
	timeout: number;
} => {
	const getBaseSlotCfgWithSizeMapping = (
		slotCfg: DisplaySlotConfig,
		pageAdUnitPath: string,
	) => ({
		slotID: slotCfg.slotId,
		slotName: slotCfg.adUnitPath
			? `${slotCfg.adUnitPath}/${slotCfg.slotId}`
			: `${pageAdUnitPath}/${slotCfg.slotId}`,
		...(slotCfg.sizeMapping && { sizeMapping: slotCfg.sizeMapping }),
	});

	const getConfiguredSlotSize = (
		slotCfg: ReturnType<typeof getBaseSlotCfgWithSizeMapping>,
	) => ({
		...slotCfg,
		sizes: getSizeForSlot(slotCfg.slotID, adSizesAmazon) as [number, number][],
	});

	const slotsAmzn: AmazonSlotMapEntry[] = displaySlots
		// restrict the number of slots to avoid the apstag error '413 Payload too large'
		.slice(0, maxSlotsPerAuction)
		.map((slotCfg) => getBaseSlotCfgWithSizeMapping(slotCfg, pageAdUnitPath))
		.map(getConfiguredSlotSize)
		.map(removeUnneededSizes)
		.map(removeSizeMapping);

	return {
		slots: slotsAmzn,
		timeout,
	};
};

const prepareVideoSlots = (
	videoSlots: VideoSlotConfig[],
	timeout: number,
	maxSlotsPerAuction: number,
): {
	slots: AmazonSlotMapEntry[];
	timeout: number;
} => {
	const slots = videoSlots.slice(0, maxSlotsPerAuction).map((slot) => ({
		slotID: "preroll",
		mediaType: "video",
		sizes: [slot.currentSize],
	}));
	return { slots, timeout };
};

// export the dependency-loader so we can reset it for unit testing
export const amazonLoader = createDependencyLoader<void, [ApsTagConfig]>(
	(config) => {
		appendHtmlToHead(apsTag);
		// We declared a global window.apstag in a ts file, altho it actually didn't exist until now.
		// That's why the following line doesn't cause a ts error.
		window.apstag.init(config);
		return Promise.resolve();
	},
);
